import { WhiteBox, FoodsSelect, RadioList, FoodsTextInput, FoodsIcon } from "@foodpilot/foods";
import { Box, Button, Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ActionPlanFormGoal, ActionPlanFormScoreOptions } from "../useActionPlanForm";
import { useEntityScores } from "src/context/Scores/useEntityScores";

type GoalFieldsProps = {
    goal: ActionPlanFormGoal;
    setGoal: (goal: ActionPlanFormGoal) => void;
    deleteGoal: (goal: ActionPlanFormGoal) => void;
    remainingScoreOptions: ActionPlanFormScoreOptions;
};
export const GoalFields = (props: GoalFieldsProps) => {
    const theme = useTheme();
    const { t } = useTranslation();

    const { scoresWithVariations } = useEntityScores();
    const { goal, setGoal, deleteGoal, remainingScoreOptions } = props;

    const variationsForGivenScore = scoresWithVariations.filter((score) => {
        return score.scoreId === goal.score?.scoreId;
    });

    return (
        <WhiteBox>
            <Stack padding={theme.spacing(2)} gap={theme.spacing(2)}>
                <Stack gap={theme.spacing(1)} position={"relative"}>
                    <Stack
                        pb={theme.spacing(2)}
                        justifyContent={"space-between"}
                        alignItems={"flex-start"}
                        direction={"row"}
                    >
                        <Typography variant="h5">{t("actionplan.trajectory.form.goal.score.title")}</Typography>
                        <Button
                            variant="ternary"
                            onClick={() => deleteGoal(goal)}
                            sx={{
                                position: "absolute",
                                right: 0,
                                minWidth: "unset",
                                width: theme.spacing(6),
                                height: theme.spacing(6),
                                p: 0,
                            }}
                        >
                            <FoodsIcon size={4} icon={"delete"} />
                        </Button>
                    </Stack>
                    <Stack pb={theme.spacing(2)} flexDirection={"row"} justifyContent={"space-between"}>
                        <Stack flexDirection={"row"} gap={theme.spacing(2)}>
                            <FoodsSelect
                                selectedOption={goal.score}
                                options={remainingScoreOptions["scores"]}
                                onChange={(selectedValue) => {
                                    setGoal({
                                        ...goal,
                                        score: selectedValue,
                                        variation: null,
                                    });
                                }}
                                getId={(item) => item.scoreId}
                                getName={(item) => item.group}
                                textOptions={{
                                    placeholder: t("actionplan.trajectory.form.goal.indicator.score"),
                                    searchPlaceholder: t("component.select.searchplaceholder"),
                                    emptyOptionsText: t("component.select.nooptions"),
                                    defaultValueText: t("component.select.defaultvalue"),
                                    noResult: t("component.select.noresultfound"),
                                }}
                            />
                            <FoodsSelect
                                selectedOption={goal.variation}
                                options={variationsForGivenScore}
                                disabled={goal.score === null}
                                onChange={(selectedValue) => {
                                    setGoal({
                                        ...goal,
                                        variation: selectedValue,
                                    });
                                }}
                                getId={(item) => item?.variationId ?? -1}
                                getName={(item) => item?.label ?? ""}
                                textOptions={{
                                    placeholder: t("actionplan.trajectory.form.goal.indicator.variation"),
                                    searchPlaceholder: t("component.select.searchplaceholder"),
                                    emptyOptionsText: t("actionplan.trajectory.form.goal.indicator.variation"),
                                    defaultValueText: t("component.select.defaultvalue"),
                                    noResult: t("component.select.noresultfound"),
                                }}
                            />
                        </Stack>
                    </Stack>
                </Stack>
                <Stack gap={theme.spacing(1)}>
                    <Typography variant="h5">{t("actionplan.trajectory.form.goal.value.title")}</Typography>
                    <Stack flexDirection={"row"}>
                        <Box sx={{ maxWidth: "350px" }}>
                            <RadioList
                                values={{
                                    percent: {
                                        name: t("actionplan.trajectory.form.goal.value.percent"),
                                        state: goal.simulationType === "percentage",
                                    },
                                }}
                                onChange={() => {
                                    setGoal({
                                        ...goal,
                                        simulationType: "percentage",
                                    });
                                }}
                            />
                        </Box>
                        <FoodsTextInput
                            value={goal.simulationVaryPercentage?.toString() ?? ""}
                            unit={"%"}
                            disabled={goal.simulationType === "absolute"}
                            onChange={(newValue: string) => {
                                const valueToUse = newValue === "" ? null : Number(newValue);
                                setGoal({
                                    ...goal,
                                    simulationVaryPercentage: valueToUse,
                                });
                            }}
                            ExtraInputProps={{
                                type: "number",
                            }}
                        />
                    </Stack>
                    <Stack flexDirection={"row"}>
                        <Box sx={{ maxWidth: "350px" }}>
                            <RadioList
                                values={{
                                    absolute: {
                                        name: t("actionplan.trajectory.form.goal.value.value"),
                                        state: goal.simulationType === "absolute",
                                    },
                                }}
                                onChange={() => {
                                    setGoal({
                                        ...goal,
                                        simulationType: "absolute",
                                    });
                                }}
                            />
                        </Box>
                        <FoodsTextInput
                            value={goal.simulationVaryAbsolute?.toString() ?? ""}
                            unit={goal.score?.unit}
                            disabled={goal.simulationType === "percentage"}
                            onChange={(newValue: string) => {
                                const valueToUse = newValue === "" ? null : Number(newValue);
                                setGoal({
                                    ...goal,
                                    simulationVaryAbsolute: valueToUse,
                                });
                            }}
                            ExtraInputProps={{
                                type: "number",
                            }}
                        />
                    </Stack>
                </Stack>
            </Stack>
        </WhiteBox>
    );
};
